import { useEffect, useRef } from "react";
import { fetchCredits, fetchOrgStats } from "../store/user";
import { fetchSession } from "../store/auth";
import { AUTH_STATUS } from "../store/auth-status";

const REFRESH_DEBOUNCE_MS = 5000;

// Single listener for all auth-related refresh triggers.
// Replaces: app.js window focus -> fetchSession
//           admin-wrapper.js visibilitychange -> fetchCredits + fetchOrgStats
//           admin-wrapper.js online -> fetchCredits + fetchOrgStats
//
// Debounces rapid tab-switching so a single user action does not fan out
// 3 requests within 100ms.
export function useSessionRefresh(authStatus, dispatch) {
    const lastRefreshRef = useRef(0);

    const trigger = (thunks) => {
        const now = Date.now();
        if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) return;
        lastRefreshRef.current = now;
        thunks.forEach((t) => dispatch(t()));
    };

    useEffect(() => {
        if (authStatus !== AUTH_STATUS.LOGGED_IN) return;

        const onFocus = () => trigger([fetchSession, fetchCredits, fetchOrgStats]);
        const onVisibility = () => {
            if (document.visibilityState === "visible") {
                trigger([fetchCredits, fetchOrgStats]);
            }
        };
        const onOnline = () => trigger([fetchCredits, fetchOrgStats]);

        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("online", onOnline);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("online", onOnline);
        };
        // trigger is stable modulo lastRefreshRef; it closes over the latest
        // dispatch reference via the outer scope, so we intentionally do not
        // include it in the dependency array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authStatus, dispatch]);
}
